import {
  Button,
  Flex,
  Modal,
  NumberInput,
  ScrollArea,
  Text,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useCallback } from "react";

import { useCustomerLateChargeFee } from "../../api/hooks/customer/useCustomerLateChargeFee.ts";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal.tsx";
import Preloader from "../../components/Preloader/Preloader.tsx";
import { UpdateLateChargeFeeInput } from "../../graphql/generated.ts";
import {
  parseStringToNumber,
  priceFormatter,
  toPercent,
} from "../../utils/number.ts";

interface Props {
  opened: boolean;
  onClose: () => void;
  onSubmit: (value: UpdateLateChargeFeeInput) => void;
}

export const ChangeLateFeeModal = ({ opened, onClose, onSubmit }: Props) => {
  const [
    confirmationModalOpened,
    { open: openConfirmationModal, close: closeConfirmationModal },
  ] = useDisclosure(false);

  const form = useForm<{
    lateChargeFee: number;
  }>({
    validate: {
      lateChargeFee: isNotEmpty("Field is required"),
    },
    validateInputOnBlur: true,
  });

  const [{ data: lateChargeFeeResponse, fetching: fetchingLateChargeFee }] =
    useCustomerLateChargeFee();
  const lateChargeFee = lateChargeFeeResponse?.customerLateChargeFee;

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }
    onSubmit({
      lateChargeFee: String(form.values.lateChargeFee),
    });
    handleClose();
  }, [form, handleClose, onSubmit]);

  return (
    <>
      <Modal
        opened={opened}
        title="Change Late Fee"
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        size="md"
        scrollAreaComponent={ScrollArea.Autosize}
        onClose={handleClose}
      >
        {fetchingLateChargeFee && <Preloader loading={true} />}
        <Flex direction="column" gap={20}>
          <Flex gap={12} direction="column">
            <Flex>
              <Text weight="500">
                Current Late Fee Percentage:{" "}
                {toPercent(lateChargeFee, "decimal", {
                  maximumFractionDigits: 5,
                })}
              </Text>
            </Flex>
            <NumberInput
              required
              hideControls
              label="New Late Fee Percentage"
              placeholder="Enter Amount"
              size="m"
              precision={5}
              parser={parseStringToNumber}
              formatter={(value) =>
                priceFormatter(value, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 5,
                })
              }
              min={0}
              {...form.getInputProps("lateChargeFee")}
            />
          </Flex>
          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={onClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              disabled={!form.isValid()}
              loading={fetchingLateChargeFee}
              onClick={openConfirmationModal}
            >
              save
            </Button>
          </Flex>
        </Flex>
      </Modal>
      {confirmationModalOpened && (
        <ConfirmationModal
          opened={true}
          message={`Are you sure you want to change the late fee percentage into ${toPercent(
            form.values.lateChargeFee,
            "percentage"
          )}?`}
          onConfirm={handleSubmit}
          onClose={closeConfirmationModal}
        />
      )}
    </>
  );
};
